import { getPatientById } from '../constants/mockPatients';
import {
  filterNotesForPatient,
  buildVitalsRows,
  buildMedicationRows,
  buildMergedFluidData,
  calculateFluidBalanceForCalendarDay,
  formatFlowSheetDateTime,
} from './flowSheetData';
import { getShiftHandoverNotesWindow, isWithinShiftHandoverWindow } from './timeHelpers';

function getNoteContent(note) {
  return note.openaiNote || note.note_content || {};
}

function formatSoapBlock(soap) {
  if (!soap) return '';
  return [
    soap.subjective && `Subjective: ${soap.subjective}`,
    soap.objective && `Objective: ${soap.objective}`,
    soap.assessment && `Assessment: ${soap.assessment}`,
    soap.plan && `Plan: ${soap.plan}`,
  ]
    .filter(Boolean)
    .join('\n');
}

function formatProgressBlock(note) {
  const time = formatFlowSheetDateTime(note.datetime);
  const content = getNoteContent(note);
  const narrative =
    typeof content.narrative === 'string' ? content.narrative.trim() : '';
  if (narrative) return `[${time}]\n${narrative}`;
  const soap = formatSoapBlock(content.soap);
  return `[${time}]\n${soap || 'No progress note content.'}`;
}

function flattenSbarToNarrative(sbar = {}) {
  return [sbar.situation, sbar.background, sbar.assessment, sbar.recommendation]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

function narrativeFromParsed(parsed) {
  if (typeof parsed?.narrative === 'string' && parsed.narrative.trim()) {
    return parsed.narrative;
  }
  return flattenSbarToNarrative(parsed?.sbar);
}


function formatVitalsBlock(rows) {
  if (!rows.length) return 'None recorded.';
  return rows
    .map((row) => {
      const parts = [
        `Time ${row.time}`,
        row.bp && `BP ${row.bp}`,
        row.pulse != null && `Pulse ${row.pulse}`,
        row.temp != null && `Temp ${row.temp}°C`,
        row.o2 != null && `O2 ${row.o2}%`,
        row.rr != null && `RR ${row.rr}`,
        row.pain != null && `Pain ${row.pain}/10`,
        row.bg != null && `BG ${row.bg}`,
      ].filter(Boolean);
      return `• ${parts.join(' | ')}`;
    })
    .join('\n');
}

function formatMarBlock(rows) {
  if (!rows.length) return 'None recorded.';
  const given = rows.filter((row) => !row.notGiven);
  const notGiven = rows.filter((row) => row.notGiven);

  const givenLines = given.map((row) => {
    const alerts = [];
    if (row.highAlert) alerts.push('HIGH ALERT');
    if (row.adverseReaction) alerts.push('ADVERSE REACTION');
    const alertText = alerts.length ? ` [${alerts.join(', ')}]` : '';
    return `• ${row.time} — ${row.medication} ${row.dose} ${row.route}${alertText}`;
  });

  const notGivenLines = notGiven.map((row) => {
    const time = row.scheduledTime || row.time || '—';
    const reason = row.deviationReason || 'reason not documented';
    return `• ${row.medication} ${time} not given — ${reason}`;
  });

  if (!givenLines.length && !notGivenLines.length) return 'None recorded.';

  return [
    ...(givenLines.length ? ['Given:', ...givenLines] : []),
    ...(notGivenLines.length
      ? [
          ...(givenLines.length ? [''] : []),
          'Not given (clinically critical — incoming nurse must know):',
          ...notGivenLines,
        ]
      : []),
  ].join('\n');
}

function formatFluidBlock(flowSheetData, sessionNotes, patientId, patientName, asOf = new Date()) {
  const merged = buildMergedFluidData(sessionNotes, patientId, patientName, flowSheetData || {});
  const { fluidInputs, fluidOutputs } = merged;
  if (!fluidInputs.length && !fluidOutputs.length) return 'None recorded.';

  const balance = calculateFluidBalanceForCalendarDay(fluidInputs, fluidOutputs, asOf);
  const inputLines = fluidInputs.map(
    (row) =>
      `• IN ${row.time || '—'}: ${row.type === 'Other' ? row.typeOther : row.type} ${row.volumeInfused || 0} ml`
  );
  const outputLines = fluidOutputs.map(
    (row) =>
      `• OUT ${row.time || '—'}: ${row.type === 'Other' ? row.typeOther : row.type} ${row.volume || 0} ml`
  );

  return [
    ...inputLines,
    ...outputLines,
    `Calendar day ${balance.dayLabel}: Net fluid balance ${balance.net} ml (Input ${balance.totalInput} ml, Output ${balance.totalOutput} ml)`,
  ].join('\n');
}

function collectFlags(notes) {
  const flags = new Set();
  notes.forEach((note) => {
    const content = getNoteContent(note);
    (content.flags || []).forEach((flag) => flags.add(flag));
    (note.flags || []).forEach((flag) => flags.add(flag));
    if (content.negations_detected?.length) {
      content.negations_detected.forEach((n) => flags.add(`Negation: ${n}`));
    }
  });
  return [...flags];
}

function formatCarePlanBlock(carePlans = [], asOf = new Date()) {
  const inWindow = (carePlans || []).filter((plan) =>
    isWithinShiftHandoverWindow(plan.datetime, asOf)
  );
  if (!inWindow.length) return 'None recorded.';

  return inWindow
    .map((plan) => {
      const content = plan.care_plan || {};
      const time = formatFlowSheetDateTime(plan.datetime);
      const status = plan.status === 'resolved' ? 'Resolved' : 'Active';
      const interventions = (content.interventions || []).filter(Boolean);
      const interventionText = interventions.length
        ? interventions.map((item) => `  - ${item}`).join('\n')
        : '  - Not documented.';
      return [
        `[${time}] Status: ${status}`,
        `Nursing Diagnosis: ${content.nursing_diagnosis || 'Not documented.'}`,
        `Goals: ${content.goals || 'Not documented.'}`,
        `Interventions:\n${interventionText}`,
        `Evaluation: ${content.evaluation || 'Not documented.'}`,
      ].join('\n');
    })
    .join('\n\n');
}

function formatDischargeBlock(dischargeNotes = []) {
  if (!dischargeNotes.length) return 'None recorded.';
  return dischargeNotes
    .map((note) => {
      const content = getNoteContent(note);
      const time = formatFlowSheetDateTime(note.datetime);
      const narrative = typeof content.narrative === 'string' ? content.narrative.trim() : '';
      if (narrative) {
        return `[${time}]\n${narrative}`;
      }
      return [
        `[${time}]`,
        content.condition_at_discharge && `Condition: ${content.condition_at_discharge}`,
        content.discharge_mode && `Mode: ${content.discharge_mode}`,
        content.accompanied_by && `Accompanied by: ${content.accompanied_by}`,
        content.follow_up?.physician && `Follow-up: ${content.follow_up.physician}`,
        content.special_instructions && `Instructions: ${content.special_instructions}`,
      ]
        .filter(Boolean)
        .join('\n');
    })
    .join('\n\n');
}

function normalizeTaskTitle(task) {
  if (task.itemLabel && task.sourceNoteType === 'medication_admin') return task.itemLabel;
  if (task.itemLabel && task.sourceNoteType === 'vitals') return `Vitals — ${task.itemLabel}`;
  return task.title || 'Task';
}

export function buildIncompleteTaskDetails(tasks = []) {
  return (tasks || [])
    .filter((t) => ['pending', 'overdue', 'ongoing', 'in_progress'].includes(t.status))
    .map((t) => ({
      id: t.id,
      title: normalizeTaskTitle(t),
      status: t.status,
      dueTime: t.dueTime || t.windowEnd || null,
      wasOverdue: t.status === 'overdue',
    }));
}

export function groupSessionNotesByPatient(sessionNotes, asOf = new Date()) {
  const groups = new Map();
  const { windowStart, shiftEnd } = getShiftHandoverNotesWindow(asOf);

  sessionNotes.forEach((note) => {
    if (note.note_type === 'handover') return;
    if (!isWithinShiftHandoverWindow(note.datetime, asOf)) return;

    const key = note.patient_id || note.patient_name || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        patientKey: String(key),
        patientId: note.patient_id || null,
        patientName: note.patient_name || 'Unknown',
        patientRoom: note.patient_room || '—',
        notes: [],
        windowStart: windowStart.toISOString(),
        windowEnd: shiftEnd.toISOString(),
      });
    }
    const group = groups.get(key);
    group.notes.push(note);
    if (!group.patientId && note.patient_id) group.patientId = note.patient_id;
    if (note.patient_room) group.patientRoom = note.patient_room;
    if (note.patient_name) group.patientName = note.patient_name;
  });

  return Array.from(groups.values()).sort((a, b) => a.patientName.localeCompare(b.patientName));
}

/**
 * Merge patients who only have Care Plans (no session notes) into the handover groups.
 */
export function mergeCarePlanPatientsIntoGroups(
  patientGroups = [],
  carePlansByPatient = {},
  admittedPatients = [],
  asOf = new Date()
) {
  const groups = [...patientGroups];
  const existingIds = new Set(groups.map((group) => group.patientId).filter(Boolean));

  Object.entries(carePlansByPatient || {}).forEach(([patientId, plans]) => {
    const inWindow = (plans || []).filter((plan) =>
      isWithinShiftHandoverWindow(plan.datetime, asOf)
    );
    if (!inWindow.length || existingIds.has(patientId)) return;

    const patient = getPatientById(patientId, admittedPatients);
    groups.push({
      patientKey: String(patientId),
      patientId,
      patientName: patient ? (patient.nameEn || patient.name) : 'Unknown',
      patientRoom: patient?.room || '—',
      notes: [],
      windowStart: getShiftHandoverNotesWindow(asOf).windowStart.toISOString(),
      windowEnd: getShiftHandoverNotesWindow(asOf).shiftEnd.toISOString(),
    });
    existingIds.add(patientId);
  });

  return groups.sort((a, b) => a.patientName.localeCompare(b.patientName));
}

export function compilePatientShiftData(
  patientGroup,
  flowSheetData,
  sessionNotes,
  outstandingTaskDetails = [],
  carePlans = [],
  asOf = new Date()
) {
  const { patientId, patientName, patientRoom } = patientGroup;
  const patientNotes = filterNotesForPatient(sessionNotes, patientId, patientName).filter((note) =>
    isWithinShiftHandoverWindow(note.datetime, asOf)
  );

  const progressNotes = patientNotes.filter((n) => n.note_type === 'progress_note');
  const dischargeNotes = patientNotes.filter((n) => n.note_type === 'discharge');
  const vitalsRows = buildVitalsRows(patientNotes, patientId, patientName);
  const marRows = buildMedicationRows(
    patientNotes,
    patientId,
    patientName,
    flowSheetData?.manualMedications || []
  );
  const flags = collectFlags(patientNotes);

  const progressBlock = progressNotes.length
    ? progressNotes.map((note) => formatProgressBlock(note)).join('\n\n')
    : 'None recorded.';

  const outstandingTitles = outstandingTaskDetails.map((task) => task.title);
  const { windowStart, shiftEnd } = getShiftHandoverNotesWindow(asOf);

  const sections = [
    `Patient: ${patientName}`,
    `Room: ${patientRoom}`,
    `Shift window: ${formatFlowSheetDateTime(windowStart)} – ${formatFlowSheetDateTime(shiftEnd)} (last 12 hours relative to shift end)`,
    '',
    '=== PROGRESS NOTES ===',
    progressBlock,
    '',
    '=== VITALS ===',
    formatVitalsBlock(vitalsRows),
    '',
    '=== MEDICATION ADMINISTRATION (MAR) ===',
    formatMarBlock(marRows),
    '',
    '=== FLOW SHEET — FLUID BALANCE ===',
    formatFluidBlock(flowSheetData, patientNotes, patientId, patientName, asOf),
    '',
    '=== NURSING CARE PLANS ===',
    formatCarePlanBlock(carePlans, asOf),
    '',
    '=== DISCHARGE ===',
    formatDischargeBlock(dischargeNotes),
    '',
    '=== FLAGS / ALERTS ===',
    flags.length ? flags.map((f) => `• ${f}`).join('\n') : 'None.',
    '',
    '=== TASKS NOT COMPLETED THIS SHIFT ===',
    outstandingTitles.length
      ? `Tasks not completed this shift (already shown separately in the UI — do NOT enumerate them in the narrative):\n${outstandingTitles.join('\n')}\n\nIn narrative: at most ONE short sentence that incomplete tasks remain (e.g. "Multiple scheduled tasks incomplete — see Outstanding Tasks."). Put the full list only in outstanding_tasks.`
      : 'No outstanding tasks.',
  ];

  return sections.join('\n');
}

export function createEmptyHandoverDraft(patientGroup) {
  return {
    patientKey: patientGroup.patientKey,
    patientId: patientGroup.patientId,
    patientName: patientGroup.patientName,
    patientRoom: patientGroup.patientRoom,
    narrative: '',
    sbar: {
      situation: '',
      background: '',
      assessment: '',
      recommendation: '',
    },
    outstanding_tasks: [],
    outstanding_task_details: [],
    pending_results: [],
    medications_due_next_shift: [],
    flags: [],
    status: 'pending',
    error: null,
    generatedAt: null,
  };
}

export function normalizeHandoverDraft(parsed, patientGroup, outstandingTaskDetails = []) {
  const draft = createEmptyHandoverDraft(patientGroup);
  const narrative = narrativeFromParsed(parsed);
  draft.narrative = narrative;
  // Mirror into sbar.situation for older report/PDF readers.
  draft.sbar = {
    situation: narrative,
    background: '',
    assessment: '',
    recommendation: '',
  };

  const parsedOutstanding = (parsed?.outstanding_tasks || [])
    .map((t) => (typeof t === 'string' ? t : t?.text || ''))
    .filter(Boolean);
  const detailTitles = (outstandingTaskDetails || []).map((t) => t.title);
  draft.outstanding_tasks = [...new Set([...parsedOutstanding, ...detailTitles])];
  draft.outstanding_task_details = outstandingTaskDetails || [];
  draft.pending_results = (parsed?.pending_results || [])
    .map((t) => (typeof t === 'string' ? t : t?.text || ''))
    .filter(Boolean);
  draft.medications_due_next_shift = (parsed?.medications_due_next_shift || [])
    .map((t) => (typeof t === 'string' ? t : t?.text || ''))
    .filter(Boolean);
  draft.flags = parsed?.flags || [];
  draft.status = 'ready';
  draft.generatedAt = new Date().toISOString();
  return draft;
}
